import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { format } from 'date-fns';
import { ref, update } from 'firebase/database';
import moment from 'moment/moment';
import { v4 as uuidv4 } from 'uuid';
import checkSvg from '../../../assets/img/check_icon.svg';
import { database } from 'Common/Firebase';
import StudentGapTask from 'Components/ChecklistTasks/StudentGapTask';
import StudentMathFormulasTask from 'Components/ChecklistTasks/StudentMathFormulasTask';
import StudentTestTask from 'Components/ChecklistTasks/StudentTestTask';
import StudentUnderlineTask from 'Components/ChecklistTasks/StudentUnderlineTask';
import StudentWordCompositionTask from 'Components/ChecklistTasks/StudentWordCompositionTask';
import StudentWordStressTask from 'Components/ChecklistTasks/StudentWordStressTask';
import { iclApi, taskApi } from '../../api';
import StudentExcludesTask from '../../Components/ChecklistTasks/StudentExludesTask';
import StudentSelectOneTask from '../../Components/ChecklistTasks/StudentSelectOneTask';
import StudentSentenceStructureTask from '../../Components/ChecklistTasks/StudentSentenceStructure';
import StudentTaskWithLink from '../../Components/ChecklistTasks/StudentTaskWithLink';
import StudentTextTask from '../../Components/ChecklistTasks/StudentTextTask';
import EditorProvider from '../../Components/ImageEditor/EditorProvider';
import { LoadingError } from '../../Components/LoadingError';
import { GapWord, Exam, TaskKeysType } from '../../shared/types';
import { ExcludeAnswer, SelectOneAnswer, SummarizeTask, TestItem } from '../../shared/types/task';
import { useDispatch, useSelector } from 'react-redux';
import { getTotalPoint, setTotalPoint } from 'redux/slices/flagsSlice';

const componentMapping = {
  text: StudentTextTask,
  link: StudentTaskWithLink,
  test: StudentTestTask,
  exclude: StudentExcludesTask,
  selectOne: StudentSelectOneTask,
  sentenceStructure: StudentSentenceStructureTask,
  gap: StudentGapTask,
  mathFormulas: StudentMathFormulasTask,
  wordComposition: StudentWordCompositionTask,
  wordStress: StudentWordStressTask,
  underline: StudentUnderlineTask,
};

interface LocationState {
  examId?: string;
}

export default function ExamStudent({ user }: any) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const location = useLocation();
  const state = location.state as LocationState | null;
  const examId: string | null = state?.examId ? state?.examId : null;

  const dispatch = useDispatch();

  const [iclData, setIclData] = useState<Exam | null>(null);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);

  const iclStatus = iclData?.status === 'checked' || iclData?.status === 'completed';

  const allCompleted = tasks.length > 0 && tasks.every(task => !!task.completedAt);
  const isDisabledSendButton = !allCompleted || iclStatus;

  useEffect(() => {
    setLoading(true);

    if (examId) {
      iclApi.getICL(examId).then(data => {
        if (data) {
          setIclData(data);
        }

        setLoading(false);
      });
    }
  }, [state]);

  useEffect(() => {
    if (examId) {
      const updateTasks = (tasks: SummarizeTask[]) => setTasks(tasks);
      const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: examId, setNewTasks: updateTasks });

      return () => {
        unsubscribe();
      };
    }
  }, [state]);

  const handleChangeTaskByKey = (
    id: string,
    key: TaskKeysType,
    value: string[] | string | boolean | ExcludeAnswer[] | TestItem[] | SelectOneAnswer[] | GapWord[],
  ) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value };

    newTaskArray.splice(index, 1, updatedObject);

    if (key === 'completedAt' && (updatedObject.completedAt || !updatedObject.completedAt)) {
      taskApi.updateTask<SummarizeTask>(examId as string, updatedObject);
    }

    setTasks(newTaskArray);
  };

  const totalPoint = useSelector(getTotalPoint);

  const totalPoints = useMemo(() => {
    return tasks.reduce((total, task) => total + (task.isCorrectAnswer ? task.points || 0 : 0), 0);
  }, [tasks]);

  useEffect(() => {
    dispatch(setTotalPoint(totalPoints));
  }, [totalPoints, dispatch]);

  const saveTaskAnswer = async (id: string) => {
    setLoading(true);

    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index] };

    newTaskArray.splice(index, 1, updatedObject);

    await taskApi.updateTask<SummarizeTask>(examId as string, updatedObject).then(() => {
      setLoading(false);
      toast.success(t('answer saved'));
    });
  };

  const renderTasks = () => {
    const activeTasks = tasks.filter(it => it.isActive);

    activeTasks.sort((a, b) => {
      if (a.indexNumber < b.indexNumber) {
        return -1;
      }

      return 0;
    });

    return activeTasks.map((it: SummarizeTask, index: number) => {
      const Component = componentMapping[it.type];

      return (
        <Component
          index={index}
          icl={iclData}
          key={it.id}
          {...it}
          handleChangeTaskByKey={handleChangeTaskByKey}
          saveTaskAnswer={saveTaskAnswer}
        />
      );
    });
  };

  const sendCompleteExamNotification = async () => {
    if (!iclData) {
      return;
    }

    const teacherEmail = iclData?.teacherEmail;
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');
    const notificationId = uuidv4();

    const notificationData = {
      id: notificationId + teacherEmail,
      idItem: teacherEmail,
      subject: iclData?.subject,
      name: user.FIO,
      time: formattedTime,
      type: 'examCompleted',
      iclId: iclData?.id,
      workType: iclData?.type,
      viewed: true,
    };

    try {
      await update(ref(database, `Teachers/${teacherEmail}/notification`), {
        [notificationId + teacherEmail]: notificationData,
      });

      const newIcl = {
        ...iclData,
        status: 'completed',
        time: formattedTime,
      };

      iclApi.updateICL(newIcl);

      toast.success(t('Successfully sended'));
    } catch (error) {
      toast.error(t('Not sended'));
      console.log(error, 'error sending notification');
    }
  };

  return (
    <>
      {tasks.length > 0 ? (
        <EditorProvider>
          <LoadingError isLoading={isLoading && !tasks}>
            <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-3 py-4 shadow-none lg:w-11/12 lg:!px-[120px] lg:!py-[40px]">
              <div className="flex w-fit flex-col gap-[12px] lg:w-full lg:gap-[20px]">
                <div className="lg:leading-12 text-[24px] font-bold leading-9 lg:text-[48px]">
                  {t('examlist')} №<span>{iclData?.number}</span>
                </div>

                <div className="text-[20px] font-bold leading-7 lg:text-[32px] lg:leading-10">
                  {t('Cource name')}: <span className="font-medium">{iclData?.courseName}</span>
                </div>

                <div className="text-[18px] font-bold leading-6 lg:text-[24px] lg:leading-8">
                  {t('theme')}: <span className="font-medium">{iclData?.theme}</span>
                </div>

                <div className="flex flex-col text-[18px] font-bold leading-6 lg:leading-8">
                  {t('Purpose of the course')}: <span className="font-medium">{iclData?.courseGoal}</span>
                </div>

                <div className="flex flex-col gap-[12px] md:flex-row md:gap-[30%] lg:flex-row lg:gap-[30%]">
                  <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                    {t('subject')}:{' '}
                    <span className="font-medium">
                      {' '}
                      {platformLang === 'ua' ? iclData?.subject.name : iclData?.subject.ruName}
                    </span>
                  </div>

                  <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                    {t('language')}: <span className="font-medium">{iclData?.language}</span>
                  </div>

                  <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                    {t('class')}: <span className="font-medium">{t(iclData?.class)}</span>
                  </div>
                </div>
                <div className="flex flex-col text-[18px] font-bold leading-6 lg:leading-8">
                  {t('pointMax')} <span className="font-medium">{100}</span>
                </div>

                {iclData?.totalPoint && (
                  <div className="flex flex-col text-[18px] font-bold leading-6 lg:leading-8">
                    {t('studentPoint')} <span className="font-medium">{iclData?.totalPoint}</span>
                  </div>
                )}

                {iclData?.type === 'exam' && (
                  <div className="flex flex-col text-[18px] font-bold leading-6 lg:leading-8">
                    {`${t('status')}: ${t(`exam status ${iclData?.status}`)}`}
                  </div>
                )}
              </div>

              <div>{renderTasks()}</div>

              <button
                type="button"
                className="btn more h-[43px] w-full bg-[#DCF0FF] !text-[16px] lg:w-[240px] lg:!text-[18px]"
                disabled={isDisabledSendButton}
                style={{ opacity: isDisabledSendButton ? 0.5 : 1, marginTop: 20, marginLeft: 'auto' }}
                onClick={() => sendCompleteExamNotification()}
              >
                {t('sendToTeacher')}
              </button>
            </div>
          </LoadingError>
        </EditorProvider>
      ) : (
        <div className="mx-auto max-w-[600px] text-center text-[24px] font-bold leading-[2rem] tracking-wide lg:text-[48px] lg:leading-[4rem]">
          {t('deletedExam')}
        </div>
      )}
    </>
  );
}
